import { useEffect, useRef, useState } from 'react';
import { apiError } from '@/lib/pos-api';
import { backupPurchaseDraft, removeLocalPurchaseDraft, writePurchaseDraft } from '@/lib/purchase-drafts';

export function usePurchaseDraft({ draft, hasContent, prefix, initialDraft }) {
  const revision = useRef(initialDraft?.revision || 0);
  const saved = useRef(null), latest = useRef(draft), queue = useRef(Promise.resolve());
  const completed = useRef(false), timer = useRef(null), mounted = useRef(true);
  const localOK = useRef(false), hasContentRef = useRef(hasContent);
  const [state, setState] = useState({ status: 'empty', error: '' });
  latest.current = draft; hasContentRef.current = hasContent;
  const update = value => { if (mounted.current) setState(value); };
  const backup = snapshot => {
    try { backupPurchaseDraft(prefix, { ...snapshot, revision: revision.current }); localOK.current = true; }
    catch { localOK.current = false; }
  };
  const persist = snapshot => {
    const pending = queue.current.catch(() => {}).then(async () => {
      if (completed.current || saved.current === snapshot) return;
      update({ status: 'saving', error: '' });
      try {
        const result = await writePurchaseDraft(snapshot, revision.current);
        revision.current = result.revision; saved.current = snapshot;
        try {
          if (latest.current === snapshot) removeLocalPurchaseDraft(prefix, snapshot.id);
          else backup(latest.current);
          if (initialDraft?.recoverySourceId) removeLocalPurchaseDraft(prefix, initialDraft.recoverySourceId);
        } catch { /* The acknowledged server copy is authoritative. */ }
        if (latest.current === snapshot) update({ status: 'saved', error: '' });
      } catch (error) {
        update({ status: localOK.current ? 'local' : 'failed', error: apiError(error) });
        throw error;
      }
    });
    queue.current = pending;
    return pending;
  };
  const flush = async () => {
    clearTimeout(timer.current);
    if (!hasContentRef.current && !revision.current) return;
    await persist(latest.current);
  };
  const actions = useRef({ flush }); actions.current = { flush };
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(timer.current);
      if (!completed.current) actions.current.flush().catch(() => {});
    };
  }, []);
  useEffect(() => {
    if (completed.current || (!hasContent && !revision.current)) return;
    backup(draft);
    update({ status: 'saving', error: '' });
    timer.current = setTimeout(() => persist(draft).catch(() => {}), 1500);
    const online = () => actions.current.flush().catch(() => {});
    const warn = event => { if (!completed.current && saved.current !== latest.current) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('online', online); window.addEventListener('beforeunload', warn);
    return () => { clearTimeout(timer.current); window.removeEventListener('online', online); window.removeEventListener('beforeunload', warn); };
    // Saver uses refs so queued writes use the latest server revision.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, hasContent, prefix]);
  const finish = () => {
    completed.current = true; clearTimeout(timer.current);
    try { removeLocalPurchaseDraft(prefix, draft.id); } catch { /* Server completed atomically. */ }
  };
  return { state, flush, finish, revision };
}
